import { CloudUpload } from '@mui/icons-material'
import { Button } from '@mui/material'
import React, { Suspense, useState } from 'react'
import { gql, useQuery } from 'urql'
import { UserConnection } from '../../schema'
import { useURLParam } from '../actions'
import CompList from '../lists/CompList'
import { CompListItemNav } from '../lists/CompListItems'
import ListPageControls from '../lists/ListPageControls'
import { UserAvatar } from '../util/avatars'
import { useSessionInfo } from '../util/RequireConfig'
import Search from '../util/Search'
import { FavoriteIcon } from '../util/SetFavoriteButton'
import UserCreateDialog from './UserCreateDialog'
import UserOnboardDialog from './UserOnboardDialog'
import UserPhoneNumberFilterContainer from './UserPhoneNumberFilterContainer'

const query = gql`
  query usersQuery($input: UserSearchOptions) {
    users(input: $input) {
      nodes {
        id
        name
        email
        isFavorite
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

const context = { suspense: false }

function UserList(): JSX.Element {
  const { isAdmin } = useSessionInfo()
  const [create, setCreate] = useState(false)
  const [onboard, setOnboard] = useState(false)
  const [search] = useURLParam<string>('search', '')
  const [cursor, setCursor] = useState('')

  const inputVars = {
    favoritesFirst: true,
    search,
    CMValue: '',
    after: cursor,
  }
  if (search.startsWith('phone=')) {
    inputVars.CMValue = search.replace(/^phone=/, '')
    inputVars.search = ''
  }

  const [q] = useQuery<{ users: UserConnection }>({
    query,
    variables: { input: inputVars },
    context,
  })
  const nextCursor = q.data?.users.pageInfo.hasNextPage
    ? q.data?.users.pageInfo.endCursor
    : ''
  // cache the next page
  useQuery({
    query,
    variables: { input: { ...inputVars, after: nextCursor } },
    context,
    pause: !nextCursor,
  })

  return (
    <React.Fragment>
      <Suspense>
        {create && <UserCreateDialog onClose={() => setCreate(false)} />}
        {onboard && <UserOnboardDialog onClose={() => setOnboard(false)} />}
      </Suspense>
      <ListPageControls
        createLabel='User'
        nextCursor={nextCursor}
        onCursorChange={setCursor}
        loading={q.fetching}
        onCreateClick={isAdmin ? () => setCreate(true) : undefined}
        extraControls={
          isAdmin ? (
            <Button
              variant='contained'
              onClick={() => setOnboard(true)}
              startIcon={<CloudUpload />}
            >
              Onboard Users
            </Button>
          ) : undefined
        }
        slots={{
          search: <Search endAdornment={<UserPhoneNumberFilterContainer />} />,
          list: (
            <CompList emptyMessage='No results'>
              {q.data?.users.nodes.map((u) => (
                <CompListItemNav
                  key={u.id}
                  title={u.name}
                  subText={u.email}
                  url={u.id}
                  action={u.isFavorite ? <FavoriteIcon /> : undefined}
                  icon={<UserAvatar userID={u.id} />}
                />
              ))}
            </CompList>
          ),
        }}
      />
    </React.Fragment>
  )
}

export default UserList
